import type { TemplatedJson } from "./connector-tool-policy-json"
import { isWithinJsonLimits } from "./connector-tool-policy-json"

export const MAX_RESOLVED_REF_LENGTH = 2_048
export const MAX_AGENT_VALUE_LENGTH = MAX_RESOLVED_REF_LENGTH

// "json" parses the resolved text, so a template can build a whole object out of
// references instead of only a string.
export const REF_CASTS = ["number", "boolean", "json"] as const
export type RefCast = (typeof REF_CASTS)[number]

/** The type names a `{{agent.<type>}}` token may end in. Mirrors JSON Schema. */
export const AGENT_TYPES = [
	"string",
	"number",
	"integer",
	"boolean",
	"null",
	"json",
] as const
export type AgentType = (typeof AGENT_TYPES)[number]

export function isAgentType(word: string): word is AgentType {
	return (AGENT_TYPES as readonly string[]).includes(word)
}

// Not bare Number(): it reads "0x10" as 16 and "" as 0, neither of which should
// become a guarded numeric argument.
export const DECIMAL_TEXT = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/

export type CastOutcome<T> =
	| { ok: true; value: T }
	| { ok: false; reason: "type" | "too-large" }

const REFUSED = { ok: false, reason: "type" } as const

function castNumberText(text: string): CastOutcome<number> {
	if (!DECIMAL_TEXT.test(text)) return REFUSED
	const parsed = Number(text)
	return Number.isFinite(parsed) ? { ok: true, value: parsed } : REFUSED
}

/** Text to the JSON scalar a type name asks for. The one definition of "is a number". */
export function castTextTo(
	text: string,
	type: Exclude<AgentType, "json">,
): CastOutcome<string | number | boolean | null> {
	const trimmed = text.trim()

	if (type === "string") return { ok: true, value: text }

	if (type === "number" || type === "integer") {
		const parsed = castNumberText(trimmed)
		if (!parsed.ok) return parsed
		if (type === "integer" && !Number.isInteger(parsed.value)) return REFUSED
		return parsed
	}

	if (type === "boolean") {
		const normalized = trimmed.toLowerCase()
		if (normalized === "true") return { ok: true, value: true }
		if (normalized === "false") return { ok: true, value: false }
		return REFUSED
	}

	if (!trimmed || trimmed.toLowerCase() === "null") {
		return { ok: true, value: null }
	}
	return REFUSED
}

/** A resolved reference's text under a rule-level cast. */
export function castRefText(
	text: string,
	cast: RefCast | undefined,
): CastOutcome<unknown> {
	if (!cast) return { ok: true, value: text }

	if (cast === "json") {
		let parsed: unknown
		try {
			parsed = JSON.parse(text.trim())
		} catch {
			return REFUSED
		}
		// Capped like a stored value: the text came from resolved references, so its
		// size is not something the workspace reviewed.
		return isWithinJsonLimits(parsed as TemplatedJson)
			? { ok: true, value: parsed }
			: { ok: false, reason: "too-large" }
	}

	return castTextTo(text, cast)
}

/**
 * What the model's own value becomes under a token's type. An undefined type
 * reproduces the untyped rendering, so an existing `{{agent}}` is unaffected.
 */
export function castAgentValue(
	raw: unknown,
	type: AgentType | undefined,
): CastOutcome<TemplatedJson> {
	if (type === "json") {
		if (typeof raw !== "object" || raw === null) return REFUSED
		const value = raw as TemplatedJson
		// A string is never parsed here: rule-level cast "json" owns that path, so the
		// two features compose instead of overlapping.
		return isWithinJsonLimits(value)
			? { ok: true, value }
			: { ok: false, reason: "too-large" }
	}

	if (type === "null") {
		if (raw === null) return { ok: true, value: null }
		return typeof raw === "string" ? castTextTo(raw, "null") : REFUSED
	}

	if (type === "number" || type === "integer") {
		if (typeof raw === "number") {
			if (!Number.isFinite(raw)) return REFUSED
			if (type === "integer" && !Number.isInteger(raw)) return REFUSED
			return { ok: true, value: raw }
		}
		return typeof raw === "string" ? castTextTo(raw, type) : REFUSED
	}

	if (type === "boolean") {
		if (typeof raw === "boolean") return { ok: true, value: raw }
		return typeof raw === "string" ? castTextTo(raw, "boolean") : REFUSED
	}

	const text = agentValueText(raw)
	return text === null ? REFUSED : { ok: true, value: text }
}

/**
 * The string an agent value renders as, or null when it is not a value a token can
 * carry. Patterns and untyped tokens both read this form.
 */
export function agentValueText(raw: unknown): string | null {
	if (typeof raw === "number" || typeof raw === "boolean") return String(raw)
	if (typeof raw !== "string" || raw.length > MAX_AGENT_VALUE_LENGTH)
		return null
	return raw
}
